import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label'
import { Switch } from '@/components/shadcn/switch'
import { MultiSelectCombobox, SelectOption } from '@/components/form-components/MultiSelectCombobox'
import { useCallback, useId, useMemo, useState } from 'react'
import { UserOrNominee } from '../../Requests/types'

type UserOrNomineeListFieldProps = {
  label: string
  userOptions: SelectOption<number>[]
  value: UserOrNominee[]
  onChange: (value: UserOrNominee[]) => void
  required?: boolean
  allowNominee?: boolean
  nomineeOnly?: boolean
}

export function UserOrNomineeListField({
  label,
  userOptions,
  value,
  onChange,
  required = false,
  allowNominee = true,
  nomineeOnly = false,
}: UserOrNomineeListFieldProps) {
  const selectedUserIds = useMemo(
    () => value.filter((entry) => entry.userId != null).map((entry) => entry.userId!),
    [value]
  )

  const nominees = useMemo(() => value.filter((entry) => entry.nominee != null), [value])

  const handleUserSelectionChange = useCallback(
    (userIds: number[]) => {
      const keptNominees = value.filter((entry) => entry.nominee != null)
      onChange([...userIds.map((userId) => ({ userId })), ...keptNominees])
    },
    [onChange, value]
  )

  const addNominee = useCallback(() => {
    onChange([
      ...value,
      { nominee: { givenName: '', familyName: '', email: '' } },
    ])
  }, [onChange, value])

  const updateNominee = useCallback(
    (nomineeListIndex: number, field: 'givenName' | 'familyName' | 'email', fieldValue: string) => {
      let seen = -1
      onChange(
        value.map((entry) => {
          if (entry.nominee == null) return entry
          seen += 1
          if (seen !== nomineeListIndex) return entry
          return { nominee: { ...entry.nominee, [field]: fieldValue } }
        })
      )
    },
    [onChange, value]
  )

  const removeNominee = useCallback(
    (nomineeIndex: number) => {
      let seen = 0
      onChange(
        value.filter((entry) => {
          if (entry.nominee == null) return true
          const keep = seen !== nomineeIndex
          seen += 1
          return keep
        })
      )
    },
    [onChange, value]
  )

  return (
    <div className="space-y-3">
      <Label>
        {label}
        {required ? ' *' : ''}
      </Label>
      {userOptions.length > 0 && !nomineeOnly && (
        <MultiSelectCombobox
          options={userOptions}
          selectedValues={selectedUserIds}
          onSelectionChange={handleUserSelectionChange}
          placeholder={`Select ${label.toLowerCase()}...`}
          searchPlaceholder="Search users..."
          renderBadgesInTrigger
        />
      )}
      {nomineeOnly && (
        <p className="text-sm text-muted-foreground">
          This organisation does not exist yet, so name the people who should be added.
        </p>
      )}
      {(allowNominee || nomineeOnly) && (
        <>
          <Button type="button" variant="outline" size="sm" onClick={addNominee}>
            {nomineeOnly || userOptions.length === 0 ? 'Add person' : 'Nominate someone not on the list'}
          </Button>
          {nominees.map((entry, index) => (
            <NomineeFields
              key={`nominee-${index}`}
              nominee={entry.nominee!}
              onChange={(field, fieldValue) => updateNominee(index, field, fieldValue)}
              onRemove={() => removeNominee(index)}
            />
          ))}
        </>
      )}
    </div>
  )
}

type NomineeFieldsProps = {
  nominee: { givenName: string; familyName: string; email: string }
  onChange: (field: 'givenName' | 'familyName' | 'email', value: string) => void
  onRemove: () => void
}

function NomineeFields({ nominee, onChange, onRemove }: NomineeFieldsProps) {
  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Nominee</span>
        <Button type="button" variant="ghost" size="sm" onClick={onRemove}>
          Remove
        </Button>
      </div>
      <Input
        placeholder="Given name"
        value={nominee.givenName}
        onChange={(e) => onChange('givenName', e.target.value)}
        aria-label="Given name"
      />
      <Input
        placeholder="Family name"
        value={nominee.familyName}
        onChange={(e) => onChange('familyName', e.target.value)}
        aria-label="Family name"
      />
      <Input
        placeholder="Email"
        type="email"
        value={nominee.email}
        onChange={(e) => onChange('email', e.target.value)}
        aria-label="Email"
      />
    </div>
  )
}

type ApplicationAssignmentRowProps = {
  userOptions: SelectOption<number>[]
  roleOptions: SelectOption<number>[]
  assignment: { userId?: number; nominee?: { givenName: string; familyName: string; email: string }; roleIds: number[] }
  onChange: (assignment: ApplicationAssignmentRowProps['assignment']) => void
  onRemove: () => void
  allowNominee?: boolean
}

export function ApplicationAssignmentRow({
  userOptions,
  roleOptions,
  assignment,
  onChange,
  onRemove,
  allowNominee = true,
}: ApplicationAssignmentRowProps) {
  const nomineeToggleId = useId()
  const [useNominee, setUseNominee] = useState(assignment.nominee != null || userOptions.length === 0)

  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">User / roles</span>
        <Button type="button" variant="ghost" size="sm" onClick={onRemove}>
          Remove
        </Button>
      </div>
      {allowNominee && userOptions.length > 0 && (
        <div className="flex items-center gap-2">
          <Switch checked={useNominee} onCheckedChange={setUseNominee} id={nomineeToggleId} />
          <Label htmlFor={nomineeToggleId}>Nominate someone not on the list</Label>
        </div>
      )}
      {(useNominee || userOptions.length === 0) ? (
        <>
          <Input
            placeholder="Given name"
            value={assignment.nominee?.givenName ?? ''}
            onChange={(e) =>
              onChange({
                ...assignment,
                userId: undefined,
                nominee: {
                  givenName: e.target.value,
                  familyName: assignment.nominee?.familyName ?? '',
                  email: assignment.nominee?.email ?? '',
                },
              })
            }
          />
          <Input
            placeholder="Family name"
            value={assignment.nominee?.familyName ?? ''}
            onChange={(e) =>
              onChange({
                ...assignment,
                userId: undefined,
                nominee: {
                  givenName: assignment.nominee?.givenName ?? '',
                  familyName: e.target.value,
                  email: assignment.nominee?.email ?? '',
                },
              })
            }
          />
          <Input
            placeholder="Email"
            type="email"
            value={assignment.nominee?.email ?? ''}
            onChange={(e) =>
              onChange({
                ...assignment,
                userId: undefined,
                nominee: {
                  givenName: assignment.nominee?.givenName ?? '',
                  familyName: assignment.nominee?.familyName ?? '',
                  email: e.target.value,
                },
              })
            }
          />
        </>
      ) : userOptions.length > 0 ? (
        <MultiSelectCombobox
          options={userOptions}
          selectedValues={assignment.userId != null ? [assignment.userId] : []}
          onSelectionChange={(ids) =>
            onChange({
              ...assignment,
              userId: ids[0],
              nominee: undefined,
            })
          }
          placeholder="Select user..."
          searchPlaceholder="Search users..."
        />
      ) : null}
      <MultiSelectCombobox
        options={roleOptions}
        selectedValues={assignment.roleIds}
        onSelectionChange={(roleIds) => onChange({ ...assignment, roleIds })}
        placeholder="Select roles..."
        searchPlaceholder="Search roles..."
        renderBadgesInTrigger
      />
    </div>
  )
}
